<template>
  <section class="listing">
    <div class="header">
      <div class="title">Vídeos educacionais</div>
      <button @click="$router.push('/educational-video/new')" class="button-new"><i class="fas fa-plus"></i><span>NOVO</span></button>
    </div>
    <form @submit.prevent class="form" style="padding: 0 0 15px">
      <div class="search-input">
        <input v-model="search" type="text" placeholder="Pesquisar..." />
        <i class="fas fa-search show-search"></i>
      </div>
      <select v-model="filterPlatform" style="margin-top: 10px; max-width: 280px">
        <option value="">Todas as plataformas</option>
        <option v-for="opt in platformOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
      </select>
    </form>
    <transition name="show" mode="out-in">
      <i v-if="loading" class="fas fa-spinner loading-big"></i>
      <div v-if="!loading" class="list-cards">
        <div v-for="v in filtered" :key="v.id" class="list list-row">
          <div class="data">
            <div class="main-data">{{ v.title }}</div>
            <div class="detail-data">{{ platformLabel(v.platform) }}</div>
            <div class="detail-data video-url">{{ v.youtubeUrl }}</div>
            <div class="detail-data">
              <span class="video-status" :class="{ inactive: !v.active }">{{ v.active ? 'ATIVO' : 'INATIVO' }}</span>
            </div>
          </div>
          <div class="actions">
            <button @click="openPlayer(v)" class="button-action" title="Assistir"><i class="fas fa-play"></i><span>PLAY</span></button>
            <button @click="$router.push(`/educational-video/${v.id}`)" class="button-action"><i class="fas fa-pen"></i><span>ALTERAR</span></button>
            <button @click="confirmRemove(v)" class="button-action"><i class="fas fa-trash-alt"></i><span>EXCLUIR</span></button>
          </div>
        </div>
        <div v-if="filtered.length === 0" class="empty-state">Nenhum video cadastrado.</div>
      </div>
    </transition>
    <EducationalVideo v-if="videoId" :id="videoId" />

    <div v-if="playerVideo" class="video-player-overlay" @click.self="closePlayer">
      <div class="video-player-box">
        <div class="video-player-header">
          <span class="video-player-title">{{ playerVideo.title }}</span>
          <button type="button" class="video-player-close" @click="closePlayer" aria-label="Fechar">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="video-player-body">
          <iframe
            v-if="playerEmbedUrl"
            :key="playerEmbedUrl"
            :src="playerEmbedUrl"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          />
          <p v-else class="video-player-error">Não foi possível carregar o vídeo. Verifique a URL do YouTube.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import logger from '../../logger'
import { getAll, remove } from '../api/educationalVideo'
import EducationalVideo from './EducationalVideo.vue'

const PLATFORM_OPTIONS = [
  { value: 'profit', text: 'Profit' },
  { value: 'blackarrow', text: 'BlackArrow' },
  { value: 'ninjatrader', text: 'NinjaTrader' },
  { value: 'tradingview', text: 'TradingView' },
  { value: 'profit_yan', text: 'Profit Yan' },
  { value: 'blackarrow_yan', text: 'BlackArrow Yan' },
  { value: 'ninjatrader_yan', text: 'NinjaTrader Yan' }
]

const PLATFORM_LABELS = Object.fromEntries(PLATFORM_OPTIONS.map(o => [o.value, o.text]))

export default {
  name: 'EducationalVideos',
  components: { EducationalVideo },
  props: ['id'],
  data () {
    return {
      loading: true,
      videos: [],
      search: '',
      filterPlatform: '',
      videoId: null,
      playerVideo: null,
      platformOptions: PLATFORM_OPTIONS
    }
  },
  computed: {
    playerEmbedUrl () {
      if (!this.playerVideo || !this.playerVideo.youtubeUrl) return null
      return this.youtubeEmbedUrl(this.playerVideo.youtubeUrl)
    },
    filtered () {
      let list = this.videos
      if (this.filterPlatform) {
        list = list.filter(v => v.platform === this.filterPlatform)
      }
      if (!this.search.trim()) return list
      const q = this.search.toUpperCase()
      return list.filter(v => Object.keys(v).some(key => String(v[key]).toUpperCase().includes(q)))
    }
  },
  watch: {
    '$route' (value) {
      this.videoId = value.params.id || null
      if (!value.params.id) this.getData()
    }
  },
  async mounted () {
    this.videoId = this.id || null
    await this.getData()
    document.addEventListener('keydown', this.onPlayerKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onPlayerKeydown)
  },
  methods: {
    youtubeEmbedUrl (url) {
      const trimmed = String(url || '').trim()
      if (!trimmed) return null
      let id = null
      const watchMatch = trimmed.match(/[?&]v=([^&]+)/i)
      if (watchMatch) id = watchMatch[1]
      else {
        const shortMatch = trimmed.match(/youtu\.be\/([^?&/]+)/i)
        if (shortMatch) id = shortMatch[1]
        else {
          const embedMatch = trimmed.match(/youtube\.com\/embed\/([^?&/]+)/i)
          if (embedMatch) id = embedMatch[1]
        }
      }
      if (!id) return null
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`
    },
    openPlayer (video) {
      this.playerVideo = video
    },
    closePlayer () {
      this.playerVideo = null
    },
    onPlayerKeydown (e) {
      if (e.key === 'Escape' && this.playerVideo) this.closePlayer()
    },
    platformLabel (platform) {
      return PLATFORM_LABELS[platform] || platform || ''
    },
    async getData () {
      try {
        this.loading = true
        this.videos = await getAll()
      } catch (e) {
        logger.error(e, this)
      } finally {
        this.loading = false
      }
    },
    async confirmRemove (video) {
      if (!window.confirm(`Excluir o vídeo "${video.title}"?`)) return
      try {
        await remove(video.id)
        await this.getData()
      } catch (e) {
        logger.error(e, this)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.video-url {
  font-size: 0.85em;
  word-break: break-all;
  opacity: 0.85;
}
.video-status {
  font-weight: 600;
  color: $lp-cyan;

  &.inactive {
    color: #c0392b;
  }
}
.search-input {
  min-width: 200px;
  position: relative;
  input {
    width: 100%;
  }
  i {
    position: absolute;
    top: 11px;
    right: 6px;
    color: $lp-text-2;
    font-size: 1.5em;
  }
}

.video-player-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.75);
}

.video-player-box {
  width: 100%;
  max-width: 900px;
  background: #161b22;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.video-player-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: $lp-panel-2;
  border-bottom: 1px solid rgba($lp-accent, 0.25);
  color: #fff;
}

.video-player-title {
  flex: 1;
  font-size: 1.05em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-player-close {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 1.25em;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  &:hover {
    opacity: 0.85;
  }
}

.video-player-body {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: $lp-bg;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.video-player-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  text-align: center;
  padding: 16px;
  margin: 0;
}
</style>
